import React, { useEffect, useState, useMemo } from 'react';
import styled, { keyframes } from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, ChevronRight, ExternalLink, X } from 'lucide-react';

import Text from 'components/_UI/Text';
import Loader from 'components/_UI/Loader';
import { Column } from 'styled/alignment/Column';
import { FullyCentered } from 'styled/alignment/Center';
import colors from 'theme/colors';
import paths from 'routes/paths';
import indexPriority from 'theme/z-index';
import checkoutApi from 'api/requests/checkout';

import QRCodeModal from './QRCodeModal';

function OrderConfirmation({ orderId, onClose }) {
    const navigate = useNavigate();
    const [tickets, setTickets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedProduct, setSelectedProduct] = useState(null);

    useEffect(() => {
        async function fetchTickets() {
            try {
                const response = await checkoutApi.getOrderTickets(orderId, 'unused');
                setTickets(response.data.data);
            } catch {
                navigate(paths.user.wallet);
            } finally {
                setLoading(false);
            }
        }

        fetchTickets();
    }, [orderId, navigate]);

    const groupedProducts = useMemo(() => {
        const groups = {};

        tickets.forEach((ticket) => {
            if (!groups[ticket.product_name]) {
                groups[ticket.product_name] = [];
            }
            groups[ticket.product_name].push(ticket);
        });

        return Object.entries(groups).map(([productName, items]) => ({
            productName,
            tickets: items,
        }));
    }, [tickets]);

    return (
        <Overlay>
            <OverlayContent>
                <CloseButton onClick={onClose}>
                    <X size={20} color={colors.night} />
                </CloseButton>

                {loading ? (
                    <FullyCentered height='200px'>
                        <Loader />
                    </FullyCentered>
                ) : (
                    <Inner>
                        <SuccessBadge>
                            <CheckCircle size={28} color={colors.neutralLight} />
                        </SuccessBadge>

                        <Text
                            name="highlight"
                            type="bold"
                            color={colors.nightDark}
                            textAlign="center"
                            marginTop="16px"
                        >
                            Pedido confirmado!
                        </Text>

                        <Text
                            color={colors.night}
                            textAlign="center"
                            paddingBottom={0}
                            marginTop="8px"
                        >
                            Toque no item para ver o QR Code
                        </Text>

                        <ProductList>
                            {groupedProducts.map((product) => (
                                <ProductRow
                                    key={product.productName}
                                    onClick={() => setSelectedProduct(product)}
                                >
                                    <ProductInfo>
                                        <Text name="highlight" color={colors.nightDark} paddingBottom={0}>
                                            {product.productName}
                                        </Text>
                                        <Text name="small" color={colors.night} paddingBottom={0} marginTop="2px">
                                            {product.tickets.length === 1
                                                ? '1 unidade'
                                                : `${product.tickets.length} unidades`}
                                        </Text>
                                    </ProductInfo>
                                    <ChevronRight size={20} color={colors.night} />
                                </ProductRow>
                            ))}
                        </ProductList>

                        <WalletLink onClick={() => navigate(paths.user.wallet)}>
                            <ExternalLink size={16} />
                            Ver na carteira
                        </WalletLink>
                    </Inner>
                )}
            </OverlayContent>

            {selectedProduct && (
                <QRCodeModal
                    tickets={selectedProduct.tickets}
                    productName={selectedProduct.productName}
                    onClose={() => setSelectedProduct(null)}
                />
            )}
        </Overlay>
    );
}

export default OrderConfirmation;

const slideUp = keyframes`
    from {
        opacity: 0;
        transform: translateY(40px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
`;

const Overlay = styled.div`
    position: fixed;
    inset: 0;
    z-index: ${indexPriority.xHigh};
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: flex-end;
    justify-content: center;
`;

const OverlayContent = styled.div`
    position: relative;
    width: 100%;
    max-width: 500px;
    max-height: 85vh;
    background: ${colors.neutralLight};
    border-radius: 20px 20px 0 0;
    padding: 24px 20px;
    padding-bottom: calc(24px + env(safe-area-inset-bottom, 0px));
    overflow-y: auto;
    animation: ${slideUp} 0.3s ease-out;
`;

const CloseButton = styled.button`
    position: absolute;
    top: 16px;
    right: 16px;
    width: 32px;
    height: 32px;
    border-radius: 50%;
    border: none;
    background: ${colors.neutralDark};
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    z-index: 1;
`;

const Inner = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
`;

const SuccessBadge = styled(FullyCentered)`
    width: 56px;
    height: 56px;
    border-radius: 50%;
    background-color: ${colors.successDark};
`;

const ProductList = styled.div`
    width: 100%;
    margin-top: 20px;
    display: flex;
    flex-direction: column;
    gap: 10px;
`;

const ProductRow = styled.button`
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    padding: 14px 16px;
    background-color: ${colors.neutralDark};
    border: none;
    border-radius: 10px;
    cursor: pointer;
    transition: opacity 0.2s;

    &:hover {
        opacity: 0.85;
    }
`;

const ProductInfo = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
`;

const WalletLink = styled.button`
    display: flex;
    align-items: center;
    gap: 8px;
    margin-top: 24px;
    padding: 12px 0;
    background: none;
    border: none;
    cursor: pointer;
    color: ${colors.occasionalPurple};
    font-size: 14px;
    font-weight: 600;
`;
